/**
 * usePrintFileIssueRecheck - re-run the self-heal decision on existing
 * `file_missing` print-file issues, on demand (no upload).
 *
 * Each re-check calls `trpc.printAgentAdmin.recheckPrintFileIssue`, which:
 *   1. checks the ACTIVE cloud — present now → resolve,
 *   2. else copies from the location's BACKUP cloud → resolve (self-heal),
 *   3. else re-fetches from source (custom-source heal / designs-folder heal)
 *      — `requeued` when a non-PNG (.arx4/DTG) backend re-queued the bridge,
 *      else `still_missing`.
 *
 * `recheckMany` fans out across the selection with bounded concurrency
 * (CONCURRENCY) — mirroring `usePrintFileIssueBulkUpload`'s runner. **One
 * issue's failure must never abort the batch**: a thrown error is isolated and
 * surfaced as a `still_missing` result carrying the error message.
 */

import { useCallback } from 'react';
import { trpc } from '@/lib/trpc';
import { getUserMessage } from '@/lib/trpc-error-utils';

/** Concurrency cap for the client fan-out (mirrors the bulk-upload runner). */
const CONCURRENCY = 4;

// `requeued` = a non-PNG (.arx4/DTG) backend heal reset the bridge's fetch
// attempts — the bridge re-fetch IS the source fetch there, so the issue is
// left OPEN and auto-fetching, same as `selfHealCustomNow`'s `requeued`.
// `healing` = a custom-source heal is actively running but hasn't settled yet
// — the server's 20s recheck deadline elapsed before it did, OR a duplicate
// concurrent recheck for the same issue found one already in flight, OR the
// server's concurrency cap was hit: nothing has been confirmed written
// anywhere yet, it's still fetching/resizing.
// `wrong_data_needs_upload` (BLU-651 A1) = the issue is a wrong-data reason
// (wrong_design / wrong_position / …): the file EXISTS but is WRONG, so a
// cloud-presence re-check proves nothing — the server refuses to probe and
// the issue resolves only via a corrected upload / design-data fix.
// `metadata_mismatch` = a legacy missing-file issue was reclassified after
// refresh; the operator must correct product metadata, never upload to the
// mismatched lookup filename.
// `metadata_sync_failed` (BLU-855) = the corrected print metadata is saved
// locally but Shopify never confirmed it. The row must not close on a
// composition Shopify disagrees with, so the re-check reports the stall and
// the operator saves the metadata again.
// `platen_variant_file_needed` = the size is on a platen variant (platen size
// rules) and has no platen variant file yet: a generic file must never close
// it, so the issue stays open until the size's own file is uploaded in
// Product updates (or, for a 3PL client's issue, by the client).
//
// MUST stay a superset of the router's return union
// (`printAgentAdmin.recheckPrintFileIssue`) — a status the server can send and
// this type cannot name is a silent fall-through to "still missing".
export type RecheckStatus =
  | 'resolved'
  | 'healed'
  | 'still_missing'
  | 'requeued'
  | 'healing'
  | 'metadata_mismatch'
  | 'metadata_sync_failed'
  | 'wrong_data_needs_upload'
  | 'platen_variant_file_needed';

export interface RecheckResult {
  issueId: string;
  status: RecheckStatus;
  /** Failure detail when the re-check threw (treated as still_missing). */
  error?: string;
}

interface UseRecheckResult {
  /** Re-check a single issue. Throws are propagated for single-row callers. */
  recheckOne: (issueId: string) => Promise<{ status: RecheckStatus }>;
  /**
   * Re-check many issues with bounded concurrency. Settles every issue (never
   * throws); a per-issue throw becomes a `still_missing` result with `error`.
   * `onResult` is invoked as each issue settles for live progress.
   */
  recheckMany: (
    issueIds: string[],
    onResult?: (result: RecheckResult) => void
  ) => Promise<RecheckResult[]>;
}

export function usePrintFileIssueRecheck(): UseRecheckResult {
  const recheckMutation = trpc.printAgentAdmin.recheckPrintFileIssue.useMutation();

  const recheckOne = useCallback(
    async (issueId: string): Promise<{ status: RecheckStatus }> => {
      const { status } = await recheckMutation.mutateAsync({ issueId });
      return { status };
    },
    [recheckMutation]
  );

  const recheckMany = useCallback(
    async (
      issueIds: string[],
      onResult?: (result: RecheckResult) => void
    ): Promise<RecheckResult[]> => {
      const results: RecheckResult[] = [];
      if (issueIds.length === 0) return results;

      // Process one issue, always settling (never throwing).
      const processOne = async (issueId: string): Promise<RecheckResult> => {
        try {
          const { status } = await recheckMutation.mutateAsync({ issueId });
          return { issueId, status };
        } catch (err) {
          // A single issue's failure never aborts the batch.
          return { issueId, status: 'still_missing', error: getUserMessage(err) };
        }
      };

      // Bounded-concurrency worker pool over a shared cursor.
      let cursor = 0;
      const worker = async () => {
        for (;;) {
          const index = cursor++;
          if (index >= issueIds.length) return;
          const result = await processOne(issueIds[index]!);
          results.push(result);
          onResult?.(result);
        }
      };
      const workers = Array.from(
        { length: Math.min(CONCURRENCY, issueIds.length) },
        () => worker()
      );
      await Promise.all(workers);

      return results;
    },
    [recheckMutation]
  );

  return { recheckOne, recheckMany };
}
